import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Flex, Input, List, Radio, Skeleton, Steps, Typography, message } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDate, orDash } from '../../lib/format';
import { useDebounced } from '../../lib/use-debounced';
import { listCustomers, mergeCustomers, previewMerge, type Customer } from './api';
import { stageLabel } from './stage';

const { Text } = Typography;

type Step = 0 | 1 | 2 | 3;

/**
 * Every cached screen that may name either customer: the customer pages, and
 * the lead, quotation, booking, delivery, job card, my-jobs, unit, financing,
 * registration, counter sale, invoice, till and accounts lists and pages (some
 * persisted offline for hours) that embed a customer's uid and name. String
 * prefixes, not imports, so no feature boundary is crossed.
 */
const PREFIXES_NAMING_A_CUSTOMER = [
  'customers',
  'customer',
  'leads',
  'lead',
  'quotations',
  'quotation',
  'bookings',
  'booking',
  'deliveries',
  'delivery',
  'job-cards',
  'job-card',
  'counter-orders',
  'invoices',
  'invoice',
  'workshop-search',
  'my-jobs',
  'units',
  'unit',
  'financing',
  'registration-cases',
  'registration-case',
  'accounting',
  'till-session',
  'cases',
  'follow-ups',
  'reminders',
] as const;

export function invalidateAfterMerge(queryClient: QueryClient): void {
  for (const prefix of PREFIXES_NAMING_A_CUSTOMER) {
    void queryClient.invalidateQueries({ queryKey: [prefix] });
  }
}

/** Non-whitespace characters a reason needs, matching the API's merge check. */
const MIN_REASON = 3;
const reasonLength = (reason: string): number => reason.replace(/\s/g, '').length;

/**
 * Human name for a table the merge moves rows in. A table this bundle has no
 * name for (a module registered a repointer after this was built) shows by its
 * table name rather than disappearing — the user must see everything that moves.
 */
export function movedTableLabel(t: ReturnType<typeof useTranslation>['t'], table: string): string {
  return t(`customers.merge.tables.${table}`, { defaultValue: table });
}

function CompareCard({
  customer,
  label,
  keep,
  onKeep,
}: {
  customer: Customer;
  label: string;
  keep: boolean;
  onKeep: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const rows: [string, string][] = [
    [t('fields.phone'), customer.phone],
    [t('fields.email'), orDash(customer.email)],
    [t('fields.idNumber'), orDash(customer.idNumber)],
    [t('customers.added'), formatDate(customer.createdAt)],
    [t('fields.stage'), stageLabel(customer.lifecycleStage)],
  ];
  return (
    <Card
      size="small"
      style={{ flex: '1 1 190px', borderColor: keep ? 'var(--primary)' : undefined }}
      title={
        <Flex vertical>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
          <Text strong>{customer.name}</Text>
          <Text type="secondary">{customer.customerNo}</Text>
        </Flex>
      }
    >
      <Flex vertical gap={6}>
        {rows.map(([name, value]) => (
          <Flex key={name} vertical>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {name}
            </Text>
            <Text>{value}</Text>
          </Flex>
        ))}
        <Radio checked={keep} onChange={onKeep} style={{ marginTop: 8 }}>
          {t('customers.merge.keepThis')}
        </Radio>
      </Flex>
    </Card>
  );
}

function MovedCounts({ moved }: { moved: Record<string, number> }): ReactNode {
  const { t } = useTranslation();
  const entries = Object.entries(moved);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  return (
    <Flex vertical gap={8}>
      <List
        size="small"
        bordered
        dataSource={entries}
        renderItem={([table, count]) => (
          <List.Item extra={<Text strong={count > 0}>{count}</Text>}>
            <Text type={count > 0 ? undefined : 'secondary'}>{movedTableLabel(t, table)}</Text>
          </List.Item>
        )}
      />
      {total === 0 && <Text type="secondary">{t('customers.merge.nothingToMove')}</Text>}
    </Flex>
  );
}

/**
 * "Merge with another customer", in four steps: find the other record,
 * compare and choose which survives, see what will move, give a reason.
 *
 * Step 3's counts come from the API's merge preview, which runs the real merge
 * and rolls it back, so what is shown is what the merge then does.
 */
export function MergeCustomerDrawer({
  customer,
  open,
  onClose,
  onMerged,
}: {
  customer: Customer;
  open: boolean;
  onClose: () => void;
  onMerged: (survivingUid: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(0);
  const [search, setSearch] = useState('');
  const [other, setOther] = useState<Customer | null>(null);
  const [survivorUid, setSurvivorUid] = useState(customer.uid);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStep(0);
      setSearch('');
      setOther(null);
      setSurvivorUid(customer.uid);
      setReason('');
      setFailure(null);
    }
  }, [open, customer.uid]);

  const q = useDebounced(search.trim(), 300);
  const results = useQuery({
    queryKey: ['customers', 'merge-search', q],
    queryFn: () => listCustomers({ q }),
    enabled: open && step === 0 && q.length >= 2,
  });
  // The record that is open cannot be merged into itself, so it is never offered.
  const candidates = (results.data?.items ?? []).filter((c) => c.uid !== customer.uid);

  const surviving = other === null || survivorUid === customer.uid ? customer : other;
  const merged = other === null ? null : survivorUid === customer.uid ? other : customer;

  const preview = useQuery({
    queryKey: ['customers', 'merge-preview', surviving.uid, merged?.uid],
    queryFn: () => previewMerge({ survivingUid: surviving.uid, mergedUid: merged?.uid ?? '' }),
    enabled: open && step >= 2 && merged !== null,
    // Always fresh: rows can be added to either record between two opens.
    gcTime: 0,
    staleTime: 0,
  });

  const previewError =
    preview.error instanceof ApiError
      ? preview.error.message
      : preview.error !== null
        ? t('customers.merge.previewFailed')
        : null;

  const doMerge = async (): Promise<void> => {
    if (merged === null) return;
    setSubmitting(true);
    setFailure(null);
    try {
      const result = await mergeCustomers({
        survivingUid: surviving.uid,
        mergedUid: merged.uid,
        reason: reason.trim(),
      });
      void message.success(t('customers.merge.done', { name: surviving.name }));
      // Leave the merged-away page first: refetching it while it is still on
      // screen flashes "not found" before the redirect.
      onMerged(result.survivingUid);
      invalidateAfterMerge(queryClient);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : t('customers.merge.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const next = (): void => {
    if (step === 3) {
      void doMerge();
      return;
    }
    setFailure(null);
    setStep((step + 1) as Step);
  };

  const submitLabel = [
    t('customers.merge.toCompare'),
    t('customers.merge.toPreview'),
    t('customers.merge.toReason'),
    t('customers.merge.confirm', { name: surviving.name }),
  ][step];

  const submitDisabled =
    (step === 0 && other === null) ||
    (step === 2 && (preview.data === undefined || previewError !== null)) ||
    (step === 3 && reasonLength(reason) < MIN_REASON);

  return (
    <ActionDrawer
      open={open}
      title={t('customers.merge.title')}
      submitLabel={submitLabel ?? ''}
      submitting={submitting}
      submitDisabled={submitDisabled}
      danger={step === 3}
      onSubmit={next}
      onClose={onClose}
      error={failure !== null ? <Alert type="error" showIcon message={failure} /> : undefined}
      consequence={
        step === 3 && merged !== null
          ? t('customers.merge.consequence', {
              merged: merged.name,
              mergedNo: merged.customerNo,
              surviving: surviving.name,
            })
          : undefined
      }
    >
      <Steps
        size="small"
        current={step}
        responsive={false}
        items={[
          { title: t('customers.merge.steps.find') },
          { title: t('customers.merge.steps.compare') },
          { title: t('customers.merge.steps.check') },
          { title: t('customers.merge.steps.confirm') },
        ]}
      />

      {step > 0 && (
        <Button
          type="link"
          style={{ alignSelf: 'flex-start', padding: 0 }}
          onClick={() => {
            setFailure(null);
            setStep((step - 1) as Step);
          }}
        >
          {t('customers.merge.back')}
        </Button>
      )}

      {step === 0 && (
        <Flex vertical gap={12}>
          <Text>{t('customers.merge.findHelp', { name: customer.name })}</Text>
          <Input.Search
            size="large"
            allowClear
            autoFocus
            placeholder={t('customers.search')}
            aria-label={t('customers.search')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
          {q.length >= 2 && results.isLoading && <Skeleton active paragraph={{ rows: 2 }} />}
          {q.length >= 2 && !results.isLoading && candidates.length === 0 && (
            <Text type="secondary">{t('customers.merge.noMatches')}</Text>
          )}
          <Radio.Group
            value={other?.uid}
            onChange={(e) => {
              setOther(candidates.find((c) => c.uid === e.target.value) ?? null);
            }}
            style={{ width: '100%' }}
          >
            <Flex vertical gap={8}>
              {candidates.map((c) => (
                <Card key={c.uid} size="small" styles={{ body: { padding: 12 } }}>
                  <Radio value={c.uid} style={{ width: '100%' }}>
                    <Flex vertical>
                      <Text strong>{c.name}</Text>
                      <Text type="secondary">
                        {c.phone} · {c.customerNo}
                      </Text>
                    </Flex>
                  </Radio>
                </Card>
              ))}
            </Flex>
          </Radio.Group>
        </Flex>
      )}

      {step === 1 && other !== null && (
        <Flex vertical gap={12}>
          <Text>{t('customers.merge.compareHelp')}</Text>
          <Flex wrap gap={12}>
            <CompareCard
              customer={customer}
              label={t('customers.merge.thisRecord')}
              keep={survivorUid === customer.uid}
              onKeep={() => {
                setSurvivorUid(customer.uid);
              }}
            />
            <CompareCard
              customer={other}
              label={t('customers.merge.otherRecord')}
              keep={survivorUid === other.uid}
              onKeep={() => {
                setSurvivorUid(other.uid);
              }}
            />
          </Flex>
        </Flex>
      )}

      {step >= 2 && merged !== null && (
        <Flex vertical gap={12}>
          <Text>{t('customers.merge.movesHelp', { merged: merged.name, surviving: surviving.name })}</Text>
          {preview.isLoading && <Skeleton active paragraph={{ rows: 3 }} />}
          {previewError !== null && <Alert type="error" showIcon message={previewError} />}
          {preview.data !== undefined && <MovedCounts moved={preview.data.moved} />}
        </Flex>
      )}

      {step === 3 && (
        <Flex vertical gap={4}>
          <Text strong>{t('fields.reason')}</Text>
          <Input.TextArea
            rows={3}
            maxLength={255}
            aria-label={t('fields.reason')}
            placeholder={t('customers.merge.reasonPlaceholder')}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <Text type="secondary">{t('customers.merge.reasonHelp')}</Text>
        </Flex>
      )}
    </ActionDrawer>
  );
}
